import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface ExpansionPath {
  title: string;
  description: string;
  timeline: string;
}

export function PlatformPotential() {
  const { t, tArray } = useTranslation();
  const expansionPaths = tArray<ExpansionPath>('platformPotential.expansionPaths');

  return (
    <section className="bg-slate-100 dark:bg-[#0a0f14] py-20 lg:py-28 relative overflow-hidden">
      {/* Abstract network background */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 800 600">
          <g stroke="#5a8a9a" strokeWidth="0.5" fill="none">
            {/* Network nodes and connections */}
            <circle cx="100" cy="100" r="3" fill="#5a8a9a" />
            <circle cx="200" cy="150" r="3" fill="#5a8a9a" />
            <circle cx="300" cy="80" r="3" fill="#5a8a9a" />
            <circle cx="400" cy="200" r="4" fill="#7ab8c9" />
            <circle cx="500" cy="120" r="3" fill="#5a8a9a" />
            <circle cx="600" cy="180" r="3" fill="#5a8a9a" />
            <circle cx="700" cy="100" r="3" fill="#5a8a9a" />

            <circle cx="150" cy="300" r="3" fill="#5a8a9a" />
            <circle cx="250" cy="350" r="3" fill="#5a8a9a" />
            <circle cx="400" cy="300" r="5" fill="#7ab8c9" />
            <circle cx="550" cy="320" r="3" fill="#5a8a9a" />
            <circle cx="650" cy="280" r="3" fill="#5a8a9a" />

            <circle cx="100" cy="500" r="3" fill="#5a8a9a" />
            <circle cx="300" cy="480" r="3" fill="#5a8a9a" />
            <circle cx="400" cy="450" r="4" fill="#7ab8c9" />
            <circle cx="500" cy="520" r="3" fill="#5a8a9a" />
            <circle cx="700" cy="480" r="3" fill="#5a8a9a" />

            <line x1="100" y1="100" x2="200" y2="150" />
            <line x1="200" y1="150" x2="300" y2="80" />
            <line x1="300" y1="80" x2="400" y2="200" />
            <line x1="400" y1="200" x2="500" y2="120" />
            <line x1="500" y1="120" x2="600" y2="180" />
            <line x1="600" y1="180" x2="700" y2="100" />

            <line x1="150" y1="300" x2="250" y2="350" />
            <line x1="250" y1="350" x2="400" y2="300" />
            <line x1="400" y1="300" x2="550" y2="320" />
            <line x1="550" y1="320" x2="650" y2="280" />

            <line x1="100" y1="500" x2="300" y2="480" />
            <line x1="300" y1="480" x2="400" y2="450" />
            <line x1="400" y1="450" x2="500" y2="520" />
            <line x1="500" y1="520" x2="700" y2="480" />

            <line x1="400" y1="200" x2="400" y2="300" />
            <line x1="400" y1="300" x2="400" y2="450" />
          </g>
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div
          className="mb-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('platformPotential.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto tracking-tight">
            {t('platformPotential.title')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-base md:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
            {t('platformPotential.description')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {expansionPaths.map((path, index) => (
            <motion.div
              key={path.title}
              className="relative p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0d1318]/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-slate-300 dark:hover:border-[#3d5a6e]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              {/* Top accent line */}
              {/* <div
                className="absolute top-0 left-1 h-1 rounded-t-full bg-linear-to-r from-[#3d6a7a] to-[#5a8a9a]"
                style={{ width: `${33 + index * 22}%` }}
              /> */}

              <span className="text-[#3d6a7a] dark:text-[#7ab8c9] text-xs md:text-sm tracking-wider uppercase mb-4 block font-medium">
                {path.timeline}
              </span>
              <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-xl md:text-2xl mb-4">
                {path.title}
              </h3>
              <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed">
                {path.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Long-term vision */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="inline-block p-8 lg:p-10 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white/80 dark:bg-[#0a0f14]/50 backdrop-blur-sm">
            <p className="font-display text-slate-700 dark:text-[#c8d0d2] text-xl md:text-2xl leading-relaxed max-w-2xl">
              {t('platformPotential.quote')}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
